import { avatarSrc } from "../lib/api";
import { FRAME_STYLES } from "./PlayerAvatar";

// Shared cinematic black & gold identity assets (see /app/ASSETS_MANIFEST.md).
const STADIUM = "/assets/stadium-backdrop.jpg";
const SILHOUETTE = "/assets/player-silhouette.jpg";

// Deterministic placeholder pool — the existing clean, text-free cinematic
// black & gold portrait pack (12 portraits, already in house style). A player
// without an uploaded photo is assigned ONE stable portrait via a hash of their
// wsbf_player_id, so the same player shows the same placeholder on every surface
// (banner / card / rankings thumb) and across reloads. These are generic WSTF
// visuals — never tied to the player's real name/identity.
const POOL = [
  "/assets/home/players/player-01.webp", "/assets/home/players/player-02.webp",
  "/assets/home/players/player-03.webp", "/assets/home/players/player-04.webp",
  "/assets/home/players/player-05.webp", "/assets/home/players/player-06.webp",
  "/assets/home/players/player-07.webp", "/assets/home/players/player-08.webp",
  "/assets/home/players/player-09.webp", "/assets/home/players/player-10.webp",
  "/assets/home/players/player-11.webp", "/assets/home/players/player-12.webp",
];

function placeholderFor(player) {
  const key = player?.wsbf_player_id || player?.id || player?.display_name || "";
  if (!key) return SILHOUETTE;
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return POOL[h % POOL.length];
}

// Interim treatment for a RAW uploaded photo (no cinematic AI version yet):
// natural colour, warm, alive — NEVER grayscale. A low-opacity gold vignette
// blends it into the black & gold surfaces. This is also the permanent safe
// fallback when the AI transform is unavailable.
const INTERIM_FILTER = "saturate(0.95) contrast(1.02) brightness(1.0)";

/**
 * CinematicPortrait — the player's identity image across all surfaces.
 * Priority: AI cinematic photo (shown AS-IS, already fully treated) →
 * raw uploaded photo (natural colour + warm interim treatment) →
 * deterministic pool placeholder portrait.
 * variant="banner" : large hero banner (edge fades blend into the header).
 * variant="card"   : fixed identity block.
 * variant="thumb"  : compact leaderboard/list thumbnail.
 */
export function CinematicPortrait({ player, variant = "card", className = "", style, testid }) {
  const src = avatarSrc(player?.avatar_url);
  const isCinematic = !!player?.avatar_cinematic; // AI result: arrives fully treated
  const placeholder = src ? null : placeholderFor(player);

  // Equipped cosmetic frame (if any) — preserved across all non-banner surfaces.
  const frame = FRAME_STYLES[player?.equipped_frame];
  const frameRing = frame ? frame.ring : "ring-1 ring-[#E5B842]/40";
  const frameStyle = frame ? { ...(style || {}), boxShadow: frame.glow } : style;

  // A real uploaded photo needs interim CSS only when it is NOT already cinematic.
  const photoFilter = src && !isCinematic ? INTERIM_FILTER : "none";
  const showInterimVignette = src && !isCinematic;

  if (variant === "thumb") {
    return (
      <div className={`relative overflow-hidden rounded-md ${frameRing} bg-[#0d0b05] ${className}`} style={frameStyle} data-testid={testid} data-frame={player?.equipped_frame || undefined}>
        <img
          src={src || placeholder}
          alt={player?.display_name || ""}
          loading="lazy"
          className="w-full h-full object-cover object-top"
          style={{ filter: photoFilter }}
        />
        {showInterimVignette && <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 120% at 50% 20%, transparent 55%, rgba(212,175,55,0.22))" }} />}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(10,9,5,0.5), transparent 65%)" }} />
      </div>
    );
  }

  const isBanner = variant === "banner";
  // Cinematic images already carry their own stadium background, so the shared
  // backdrop layer is only needed behind raw photos and (harmlessly) placeholders.
  const needsBackdrop = !isCinematic;
  return (
    <div className={`overflow-hidden bg-[#0a0a0a] ${isBanner ? "" : `relative rounded-xl ${frameRing}`} ${className}`} style={isBanner ? style : frameStyle} data-testid={testid} data-frame={!isBanner && player?.equipped_frame ? player.equipped_frame : undefined}>
      {needsBackdrop && (
        <>
          <img src={STADIUM} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(70% 90% at 70% 15%, rgba(212,175,55,0.18), transparent 60%)" }} />
        </>
      )}

      {src ? (
        <>
          <img
            src={src}
            alt={player?.display_name || ""}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover object-top"
            style={{ filter: photoFilter }}
          />
          {showInterimVignette && <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(110% 110% at 60% 15%, transparent 50%, rgba(212,175,55,0.20))" }} />}
        </>
      ) : (
        <img
          src={placeholder}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
      )}

      {/* Edge fades blending the portrait into the charcoal header */}
      {isBanner && (
        <>
          <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to right, #000000 0%, rgba(0,0,0,0.6) 18%, transparent 55%)" }} />
          <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent 55%, #000000 100%)" }} />
          <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top, transparent 70%, rgba(0,0,0,0.55) 100%)" }} />
        </>
      )}
    </div>
  );
}
